import React from 'react';
import Translate from '../../../components/Translate';
import './styles.css';

/*
 * An operator's level, as a mark rather than a number.
 *
 * The number on its own is not something anyone can place: level 4 means
 * nothing until you know there are ten of them and where you sit. A mark that
 * climbs - a seedling, a star, a rocket, a crown - says "there is further to go"
 * at a glance, which is the whole point of having levels at all.
 *
 * My Space already did this, but only from level 6 up: everybody below it saw a
 * bare number, which is exactly backwards - the people who most need to see that
 * the ladder continues are the ones at the bottom of it. Every level has a mark
 * here, and they are the ones that were already in use.
 *
 * Except level 9, which was a trophy. Operator of the month is a trophy too, and
 * it is worn in the header and shown in the profile right beside this badge -
 * two different things with one picture, one of them earned once and the other
 * standing for how somebody is doing this month. A diamond instead: rare, and
 * plainly under the crown.
 */
const LEVEL_MARKS = {
    1: '🌱',
    2: '🔰',
    3: '⭐',
    4: '✨',
    5: '🔥',
    6: '🎯',
    7: '🌟',
    8: '🚀',
    9: '💎',
    10: '👑',
};

const LevelBadge = ({level, withLabel = false, className = ''}) => {
    const value = parseInt(level, 10);
    if (isNaN(value)) return null;

    /*
     * Nothing yet, and said so.
     *
     * Zero is what the server answers for an operator who has not handled enough
     * conversations for the figures to mean anything. Drawing level 1 for them
     * would be a claim we cannot make - and drawing nothing at all would leave a
     * hole in the row where every other operator has a badge, which reads as a
     * bug rather than as "not yet".
     */
    if (value <= 0) {
        return (
            <span className={'level-badge level-badge--none ' + className}>
                <span className="level-badge__mark" aria-hidden="true">—</span>
                {withLabel && <span className="level-badge__label"><Translate text="Level"/></span>}
            </span>
        );
    }

    // anything outside the ladder takes the nearest end of it rather than
    // rendering a badge with a hole where its mark should be
    const mark = LEVEL_MARKS[Math.min(10, Math.max(1, value))];

    return (
        <span className={'level-badge ' + className}>
            <span className="level-badge__mark" aria-hidden="true">{mark}</span>
            {withLabel && <span className="level-badge__label"><Translate text="Level"/></span>}
            <span className="level-badge__value">{value}</span>
        </span>
    );
};

export default LevelBadge;



// WEBPACK FOOTER //
// ./src/components-new/general/LevelBadge/index.jsx